import { differenceInCalendarDays, startOfToday, startOfYear, subDays } from 'date-fns';
import { ReactElement, useRef, useState } from 'react';
import {
  Button,
  CaptionDropdowns,
  CaptionProps,
  DayModifiers,
  DayPicker,
  IconLeft,
  IconRight,
  Matcher,
  useDayPicker,
  useNavigation,
} from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { clampMonth, EARLIEST_SELECTABLE_DATE } from '../../../helpers/DateInputHelper';
import CustomDropdown from '../DropDowns/CustomDropdown';
import DateTextInput from './DateTextInput';
import './Selector.scss';

type DateRangeField = 'start' | 'end';

type DateRangePreset = {
  label: string;
  getStartDate: (today: Date) => Date;
};

// Each preset ends today and counts today as one of its days.
const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { label: 'Last 7 days', getStartDate: (today) => subDays(today, 6) },
  { label: 'Last 30 days', getStartDate: (today) => subDays(today, 29) },
  { label: 'Last 90 days', getStartDate: (today) => subDays(today, 89) },
  { label: 'Year to date', getStartDate: (today) => startOfYear(today) },
];

/**
 * Determine which days to highlight. Range shading is only applied to a complete, correctly
 * ordered range; otherwise each chosen date is highlighted on its own.
 */
const getRangeHighlight = (
  fromDate: Date | undefined,
  toDate: Date | undefined,
): { selected: Matcher[]; modifiers: DayModifiers } => {
  if (!fromDate || !toDate || differenceInCalendarDays(toDate, fromDate) < 0) {
    return {
      selected: [fromDate, toDate].filter((date): date is Date => date !== undefined),
      modifiers: {},
    };
  }
  return {
    selected: [{ from: fromDate, to: toDate }],
    modifiers: {
      range_start: fromDate,
      range_end: toDate,
      range_middle: { after: fromDate, before: toDate },
    },
  };
};

/**
 * Calendar caption with month and year dropdowns plus previous/next buttons. react-day-picker 8.0
 * renders either the dropdowns or the buttons, not both.
 */
const DropdownCaption = (props: CaptionProps): ReactElement => {
  const { displayMonth, id } = props;
  const { classNames, onMonthChange } = useDayPicker();
  const { goToMonth, nextMonth, previousMonth } = useNavigation();

  const navigateTo = (targetMonth: Date | undefined): void => {
    if (!targetMonth) return;
    goToMonth(targetMonth);
    if (onMonthChange) onMonthChange(targetMonth);
  };

  return (
    <div className={classNames.caption}>
      <CaptionDropdowns displayMonth={displayMonth} id={id} />
      <div className={classNames.nav}>
        <Button
          aria-label="Go to previous month"
          className={`${classNames.nav_button} ${classNames.nav_button_previous}`}
          disabled={!previousMonth}
          onClick={() => navigateTo(previousMonth)}
        >
          <IconLeft className={classNames.nav_icon} />
        </Button>
        <Button
          aria-label="Go to next month"
          className={`${classNames.nav_button} ${classNames.nav_button_next}`}
          disabled={!nextMonth}
          onClick={() => navigateTo(nextMonth)}
        >
          <IconRight className={classNames.nav_icon} />
        </Button>
      </div>
    </div>
  );
};

type DateRangeInputDropdownProps = {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  setFromDate: (date: Date | undefined) => void;
  setToDate: (date: Date | undefined) => void;
  onEntryError: (message: string | undefined) => void;
};

/*
  Start and end date inputs that share one range calendar. Focusing an input opens the calendar on
  that date's month and makes it the field the next day click sets. Picking a start date moves on
  to the end date, and picking an end date closes the calendar. Either date may be left blank for
  an open-ended range.
*/
const DateRangeInputDropdown = (props: DateRangeInputDropdownProps): ReactElement => {
  const { fromDate, toDate, setFromDate, setToDate, onEntryError } = props;

  const today = startOfToday();
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState<DateRangeField>('start');
  const [month, setMonth] = useState(() =>
    clampMonth(fromDate ?? toDate ?? today, EARLIEST_SELECTABLE_DATE, today),
  );
  // Errors are tracked per input so that editing one date does not clear an error that is still
  // shown for the other. A ref keeps both current when the two inputs report in the same render,
  // as they do when a preset replaces both dates.
  const entryErrors = useRef<Partial<Record<DateRangeField, string>>>({});

  const reportEntryError = (
    field: DateRangeField,
    message: string | undefined,
  ): void => {
    entryErrors.current = { ...entryErrors.current, [field]: message };
    onEntryError(entryErrors.current.start ?? entryErrors.current.end);
  };

  const showMonthOf = (date: Date | undefined): void => {
    setMonth(clampMonth(date ?? today, EARLIEST_SELECTABLE_DATE, today));
  };

  const openCalendarFor = (field: DateRangeField): void => {
    setActiveField(field);
    showMonthOf(field === 'start' ? fromDate ?? toDate : toDate ?? fromDate);
    setShowCalendar(true);
  };

  const handleTypedDate = (field: DateRangeField, date: Date | undefined): void => {
    if (field === 'start') setFromDate(date);
    else setToDate(date);
    if (date) showMonthOf(date);
  };

  const handleDayClick = (day: Date): void => {
    reportEntryError(activeField, undefined);
    if (activeField === 'start') {
      setFromDate(day);
      // A start date after the current end date begins a new range.
      if (toDate && differenceInCalendarDays(day, toDate) > 0) setToDate(undefined);
      setActiveField('end');
      return;
    }
    setToDate(day);
    setShowCalendar(false);
  };

  const applyPreset = (preset: DateRangePreset): void => {
    entryErrors.current = {};
    onEntryError(undefined);
    setFromDate(preset.getStartDate(today));
    setToDate(today);
    setShowCalendar(false);
  };

  const rangeHighlight = getRangeHighlight(fromDate, toDate);
  // While choosing the end date, days before the start date cannot be picked.
  const disabledDays =
    activeField === 'end' && fromDate ? { before: fromDate } : undefined;

  const renderDateInput = (field: DateRangeField): ReactElement => (
    <DateTextInput
      ariaLabel={field === 'start' ? 'Start date' : 'End date'}
      isActive={showCalendar && activeField === field}
      maxDate={today}
      minDate={EARLIEST_SELECTABLE_DATE}
      onChange={(date) => handleTypedDate(field, date)}
      onEntryError={(message) => reportEntryError(field, message)}
      onFocus={() => openCalendarFor(field)}
      placeholder={field === 'start' ? 'Start' : 'End'}
      value={field === 'start' ? fromDate : toDate}
    />
  );

  return (
    <div className="date-range-picker">
      <CustomDropdown
        setShowDropdown={setShowCalendar}
        showDropdown={showCalendar}
        target={
          <div className="row date-pickers">
            {renderDateInput('start')}
            <span className="divider" />
            {renderDateInput('end')}
          </div>
        }
      >
        <div className="action-date-picker">
          <div className="date-range-presets">
            {DATE_RANGE_PRESETS.map((preset) => (
              <button
                className="date-range-preset"
                key={preset.label}
                onClick={() => applyPreset(preset)}
                type="button"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <DayPicker
            components={{ Caption: DropdownCaption }}
            disabled={disabledDays}
            footer={
              <span className="date-range-hint">
                Leave either date blank for an open-ended range.
              </span>
            }
            fromDate={EARLIEST_SELECTABLE_DATE}
            modifiers={rangeHighlight.modifiers}
            month={month}
            onDayClick={handleDayClick}
            onMonthChange={setMonth}
            selected={rangeHighlight.selected}
            toDate={today}
          />
        </div>
      </CustomDropdown>
    </div>
  );
};

export default DateRangeInputDropdown;
